import React, { useRef, useState } from "react";
import styled from "styled-components";
import CardLayout from "components/foundation/templates/CardLayout";
import Divider from "components/foundation/Divider";
import Button from "components/foundation/Button";
import AttachmentField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField/AttachmentField";
import { DOCUMENT_ALLOWED_EXTENSIONS, MAX_ATTACHMENTS } from "config/constant";
import { DocumentAttachmentFileRequest } from "apis/data-contracts";
import Modal from "components/foundation/Modal/Modal";
import { useSaveAdditionalAttachmentFiles } from "features/document/hooks/mutations/useSaveAdditionalAttachmentFiles";
import useUpdateDocumentFileUrl from "features/pdf/mutations/useUpdateDocumentFileUrl";

interface AttachedFileEditProps {
  onClose: () => void;
  attachmentsLength: number;
  documentUuid: string;
  onSuccess?: () => void;
  uploadDocPdfAndGetS3Url?: (fileName: string) => Promise<string | undefined>;
}

const AttachedFileEdit = ({
  onClose,
  attachmentsLength,
  documentUuid,
  onSuccess,
  uploadDocPdfAndGetS3Url,
}: AttachedFileEditProps) => {
  const [attachments, setAttachments] = useState<
    DocumentAttachmentFileRequest[]
  >([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const dropZoneContainerRef = useRef<HTMLDivElement>(null);

  const { mutate: saveAdditionalAttachmentFiles } =
    useSaveAdditionalAttachmentFiles();
  const { mutate: updateDocumentFileUrl } = useUpdateDocumentFileUrl();

  const handleUpdateAttachments = async () => {
    if (attachments.length === 0) {
      return;
    }

    saveAdditionalAttachmentFiles(
      {
        uuid: documentUuid,
        data: {
          documentAttachmentFiles: attachments,
        },
      },
      {
        onSuccess: async () => {
          if (uploadDocPdfAndGetS3Url) {
            try {
              const updatedS3Url = await uploadDocPdfAndGetS3Url("octo.pdf");
              if (updatedS3Url) {
                updateDocumentFileUrl({
                  uuid: documentUuid,
                  data: {
                    documentFileUrl: updatedS3Url,
                  },
                });
              }
            } catch (error) {
              console.error("PDF 업데이트 실패:", error);
            }
          }

          onSuccess?.();
          onClose();
        },
      },
    );
  };

  return (
    <>
      <DropZoneContainerStyle ref={dropZoneContainerRef}>
        <CardLayout.Container
          header={{
            title: "첨부파일 추가 첨부",
          }}
          gap={20}
          headerColor="#171D23"
          style={{
            borderRadius: "0",
            boxShadow: "none",
            padding: "0 0 2rem 0",
          }}
        >
          <AttachmentField
            attachments={attachments}
            maxAttachments={MAX_ATTACHMENTS - attachmentsLength}
            uploadParams={{
              fileType: "DOCUMENT_ATTACHMENT",
              isOverwrite: false,
              allowedExtensions: DOCUMENT_ALLOWED_EXTENSIONS,
            }}
            onChangeAttachments={(newAttachments) => {
              setAttachments(newAttachments);
            }}
            dropZoneContainerRef={dropZoneContainerRef}
          />
          <Divider />
          <ButtonWrapperStyle>
            <Button variant="tertiary" size="large" fill onClick={onClose}>
              취소
            </Button>
            <Button
              variant="primary"
              size="large"
              fill
              onClick={() => {
                setIsOpenModal(true);
              }}
            >
              완료
            </Button>
          </ButtonWrapperStyle>
        </CardLayout.Container>
      </DropZoneContainerStyle>

      <Modal.Root open={isOpenModal} onOpenChange={setIsOpenModal}>
        <Modal.Body
          onConfirm={handleUpdateAttachments}
          onCancel={() => setIsOpenModal(false)}
          title="파일을 추가 첨부하시겠습니까?"
        />
      </Modal.Root>
    </>
  );
};

export default AttachedFileEdit;

const ButtonWrapperStyle = styled.div`
  display: flex;
  gap: 1rem;
`;

const DropZoneContainerStyle = styled.div`
  position: relative;
  overflow: hidden;
  isolation: isolate;
`;
